import { omit } from "lodash";
import { AmountType, MerchandiseCatalogItemStatus, OversellPolicy } from "types";
import { VariantCatalogItem } from "./VariantTableHelper";

export interface PatchCatalogItem {
    id: string;
    status?: MerchandiseCatalogItemStatus;
    sku?: string;
    barcode?: string;
    pricePerUnit?: {
        price: number;
    };
    oversellPolicy?: OversellPolicy;
    lowInventoryThreshold?: number;
}

export const PATCH_OP_UPDATE = "update";

export interface PatchCatalogItemOperation {
    id: string;
    op: typeof PATCH_OP_UPDATE;
    data: PatchCatalogItem;
}

export interface PatchCatalogItemsPayload {
    ops: PatchCatalogItemOperation[];
}

export interface VariantCatalogIdentity {
    sku: string;
    barcode: string;
    price: number | null;
}

export interface VariantCatalogItemForPayload {
    id?: string | null;
    sku?: string | null;
    barcode?: string | null;
    price: number | null;
}

export interface VariantDraftItemValue {
    sku: string;
    barcode: string;
    price?: number | null;
}

export type VariantDraftItems = Record<string, VariantDraftItemValue>;

export const getStatusPatchCatalogItemsPayload = (
    itemIds: string[],
    status: MerchandiseCatalogItemStatus,
): PatchCatalogItemsPayload => {
    return {
        ops: itemIds.map((id) => ({
            id,
            op: PATCH_OP_UPDATE,
            data: { id, status },
        })),
    };
};

export const getPricePatchCatalogItemsPayload = (itemIds: string[], price: number): PatchCatalogItemsPayload => {
    return {
        ops: itemIds.map((id) => ({
            id,
            op: PATCH_OP_UPDATE,
            data: {
                id,
                pricePerUnit: {
                    price: Number(price),
                },
            },
        })),
    };
};

export const getOversellPolicyPatchCatalogItemsPayload = (
    itemIds: string[],
    oversellPolicy: OversellPolicy,
): PatchCatalogItemsPayload => {
    return {
        ops: itemIds.map((id) => ({
            id,
            op: PATCH_OP_UPDATE,
            data: { id, oversellPolicy },
        })),
    };
};

export const getLowInventoryPatchCatalogItemsPayload = (
    itemIds: string[],
    lowInventoryThreshold: number,
): PatchCatalogItemsPayload => {
    return {
        ops: itemIds.map((id) => ({
            id,
            op: PATCH_OP_UPDATE,
            data: { id, lowInventoryThreshold: Number(lowInventoryThreshold) },
        })),
    };
};

export const getPatchCatalogItemsPayload = (items: PatchCatalogItem[]): PatchCatalogItemsPayload => {
    return {
        ops: items.map((item) => ({
            id: item.id,
            op: PATCH_OP_UPDATE,
            data: item,
        })),
    };
};

export const getCatalogItemPrice = (item: VariantCatalogItem): number | null => {
    return item.prices?.perUnit?.price ?? item.price ?? null;
};

const isValidPrice = (price: number | null | undefined): price is number => {
    return typeof price === "number" && Number.isFinite(price);
};

export const getCatalogItemsWithDraftValues = (
    catalogItems: VariantCatalogItem[],
    variantItems: VariantDraftItems,
): VariantCatalogItem[] => {
    return catalogItems.map((item) => {
        if (!item.isLeaf || !item.id) {
            return item;
        }

        const draftItem = variantItems[item.id];
        if (!draftItem) {
            return item;
        }

        const nextSku = draftItem.sku ?? item.sku ?? "";
        const nextBarcode = draftItem.barcode ?? item.barcode ?? "";
        const effectivePrice = draftItem.price !== undefined ? draftItem.price : getCatalogItemPrice(item);

        return {
            ...item,
            sku: nextSku,
            barcode: nextBarcode,
            prices: {
                ...item.prices,
                perUnit: isValidPrice(effectivePrice)
                    ? { price: Number(effectivePrice), priceType: AmountType.ABSOLUTE }
                    : undefined,
            },
            price: isValidPrice(effectivePrice) ? effectivePrice : undefined,
        };
    });
};

export const getCatalogItemsWithSelectedStatus = (
    catalogItems: VariantCatalogItem[],
    selectedItemIds: Set<string>,
    status: MerchandiseCatalogItemStatus,
): VariantCatalogItem[] => {
    if (selectedItemIds.size === 0) {
        return catalogItems;
    }

    return catalogItems.map((item) => {
        if (!item.isLeaf || !item.id || !selectedItemIds.has(item.id)) {
            return item;
        }

        return {
            ...item,
            status,
        };
    });
};

export const getCatalogItemsWithUpdatedPrice = (
    catalogItems: VariantCatalogItem[],
    selectedLeafIds: string[],
    nextPrice: number,
): VariantCatalogItem[] => {
    const selectedLeafIdSet = new Set(selectedLeafIds);

    return catalogItems.map((item) => {
        if (!item.id || !selectedLeafIdSet.has(item.id)) {
            return item;
        }

        return {
            ...item,
            prices: {
                ...item.prices,
                perUnit: {
                    price: nextPrice,
                    priceType: AmountType.ABSOLUTE,
                },
            },
            price: nextPrice,
        };
    });
};

export const getCatalogItemsWithUpdatedOversellPolicy = (
    catalogItems: VariantCatalogItem[],
    selectedLeafIds: string[],
    oversellPolicy: OversellPolicy,
): VariantCatalogItem[] => {
    const selectedLeafIdSet = new Set(selectedLeafIds);

    return catalogItems.map((item) => {
        if (!item.id || !selectedLeafIdSet.has(item.id)) {
            return item;
        }

        return { ...item, oversellPolicy };
    });
};

export const getCatalogItemsWithUpdatedLowInventory = (
    catalogItems: VariantCatalogItem[],
    selectedLeafIds: string[],
    lowInventoryThreshold: number,
): VariantCatalogItem[] => {
    const selectedLeafIdSet = new Set(selectedLeafIds);

    return catalogItems.map((item) => {
        if (!item.id || !selectedLeafIdSet.has(item.id)) {
            return item;
        }

        return { ...item, lowInventoryThreshold };
    });
};

export const getPayloadCandidateItemsFromCatalogItems = (
    catalogItems: VariantCatalogItem[],
): VariantCatalogItemForPayload[] => {
    return catalogItems
        .filter((item) => item.isLeaf)
        .map((item) => ({
            id: item.id,
            sku: item.sku,
            barcode: item.barcode,
            price: getCatalogItemPrice(item),
        }));
};

export const getCleanedUpDraftItemsAfterBulkPriceSave = (
    selectedLeafIds: string[],
    existingVariantItems: VariantDraftItems,
): VariantDraftItems => {
    const selectedIdSet = new Set(selectedLeafIds);
    const nextVariantItems: VariantDraftItems = {};

    for (const [id, draft] of Object.entries(existingVariantItems)) {
        if (!selectedIdSet.has(id)) {
            nextVariantItems[id] = draft;
            continue;
        }

        const remainingDraft = omit(draft, "price");
        if (Object.keys(remainingDraft).length > 0) {
            nextVariantItems[id] = remainingDraft;
        }
    }

    return nextVariantItems;
};

export const getUpdatedOriginalCatalogIdentityMapWithPrice = (
    originalCatalogIdentityMap: Map<string, VariantCatalogIdentity>,
    selectedLeafIds: string[],
    nextPrice: number,
): Map<string, VariantCatalogIdentity> => {
    const nextOriginalMap = new Map(originalCatalogIdentityMap);

    for (const selectedLeafId of selectedLeafIds) {
        const previousItem = nextOriginalMap.get(selectedLeafId);
        if (!previousItem) {
            continue;
        }

        nextOriginalMap.set(selectedLeafId, {
            ...previousItem,
            price: nextPrice,
        });
    }

    return nextOriginalMap;
};

export const getUpdatedOriginalCatalogIdentityMapWithChangedItems = (
    originalCatalogIdentityMap: Map<string, VariantCatalogIdentity>,
    changedItems: PatchCatalogItem[],
): Map<string, VariantCatalogIdentity> => {
    const nextOriginalMap = new Map(originalCatalogIdentityMap);

    for (const changedItem of changedItems) {
        const previousItem = nextOriginalMap.get(changedItem.id);
        if (!previousItem) {
            continue;
        }

        nextOriginalMap.set(changedItem.id, {
            sku: changedItem.sku ?? previousItem.sku,
            barcode: changedItem.barcode ?? previousItem.barcode,
            price: changedItem.pricePerUnit?.price ?? previousItem.price,
        });
    }

    return nextOriginalMap;
};

export const getCatalogItemsWithChangedPatchItems = (
    catalogItems: VariantCatalogItem[],
    changedItems: PatchCatalogItem[],
): VariantCatalogItem[] => {
    const changedItemById = new Map(changedItems.map((changedItem) => [changedItem.id, changedItem]));

    return catalogItems.map((item) => {
        if (!item.id) {
            return item;
        }

        const changedItem = changedItemById.get(item.id);
        if (!changedItem) {
            return item;
        }

        return {
            ...item,
            ...(changedItem.sku != null ? { sku: changedItem.sku } : {}),
            ...(changedItem.barcode != null ? { barcode: changedItem.barcode } : {}),
            ...(changedItem.pricePerUnit != null
                ? {
                      prices: {
                          ...item.prices,
                          perUnit: {
                              price: changedItem.pricePerUnit.price,
                              priceType: AmountType.ABSOLUTE,
                          },
                      },
                      price: changedItem.pricePerUnit.price,
                  }
                : {}),
        };
    });
};

export const getCatalogItemsWithUpdatedItem = (
    catalogItems: VariantCatalogItem[],
    updatedItem: VariantCatalogItem,
): VariantCatalogItem[] => {
    return catalogItems.map((item) => (item.id === updatedItem.id ? updatedItem : item));
};

const getChangedCatalogItemPatchItem = (
    item: VariantCatalogItemForPayload,
    originalItem: VariantCatalogIdentity,
): PatchCatalogItem | null => {
    if (!item.id) {
        return null;
    }

    const normalizedSku = (item.sku ?? "").trim();
    const normalizedBarcode = (item.barcode ?? "").trim();
    const patchItem: PatchCatalogItem = { id: item.id };

    if (normalizedSku !== originalItem.sku) {
        patchItem.sku = normalizedSku;
    }

    if (normalizedBarcode !== originalItem.barcode) {
        patchItem.barcode = normalizedBarcode;
    }

    if (isValidPrice(item.price) && item.price !== originalItem.price) {
        patchItem.pricePerUnit = {
            price: Number(item.price),
        };
    }

    if (patchItem.sku == null && patchItem.barcode == null && patchItem.pricePerUnit == null) {
        return null;
    }

    return patchItem;
};

export const getChangedCatalogItemsPayloadItems = (
    items: VariantCatalogItemForPayload[],
    originalCatalogIdentityMap: Map<string, VariantCatalogIdentity>,
): PatchCatalogItem[] => {
    const changedItems: PatchCatalogItem[] = [];

    for (const item of items) {
        if (!item.id) {
            continue;
        }

        const originalItem = originalCatalogIdentityMap.get(item.id);
        if (!originalItem) {
            continue;
        }

        const changedPatchItem = getChangedCatalogItemPatchItem(item, originalItem);
        if (!changedPatchItem) {
            continue;
        }

        changedItems.push(changedPatchItem);
    }

    return changedItems;
};
